/**
 * A lesson and its drills, played entirely in this tab.
 *
 * The shape: pick an idea, read what it is, then find it on the board three
 * times. Naming comes first on purpose — an idea you cannot name is one you
 * cannot go looking for, and the other teaching screens all hand you facts
 * about a position without ever giving you a thing to hunt for.
 *
 * Two decisions the rest of the file follows from:
 *
 *  - **The next drill is hunted while you answer this one.** A trap position
 *    takes the better part of a second to find, and a screen that made you
 *    watch a spinner between drills would break the rhythm that makes a run of
 *    three worth doing.
 *  - **Wrong is not a dead end.** The board holds still, says what the move
 *    would have cost, and lets you look again. Nothing here is rated.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { LESSONS } from '../../../games/analysis/lessons.ts';
import type { Drill, DrillVerdict, Lesson, LessonId } from '../../../games/analysis/lessons.ts';
import { createInitial, legalMoves } from '../../../games/spec/interpreter.ts';
import { applyMove } from '../../../games/spec/interpreter.ts';
import type { SeatOf, State } from '../../../games/spec/interpreter.ts';
import type { GameSpec, RulesSpec } from '../../../games/spec/schema.ts';
import type { Seat } from '../../../shared/domain.ts';
import type { LearnRequest, LearnResponse } from './protocol.ts';

type Rules = RulesSpec;

/** How many drills make a lesson. Enough to see the pattern twice more. */
export const DRILLS_PER_LESSON = 3;

export type LearnPhase =
  /** No lesson chosen: the menu is showing. */
  | 'choosing'
  /** The idea is on screen, before the first drill. */
  | 'reading'
  /** Hunting for a position. */
  | 'seeking'
  /** A drill is up and waiting for an answer. */
  | 'asking'
  /** The answer is being judged. */
  | 'checking'
  /** Right. The explanation is showing. */
  | 'right'
  /** Wrong. The position is still there. */
  | 'wrong'
  /** All the drills are done. */
  | 'finished'
  /** Nothing could be found. */
  | 'stuck';

export interface LearnView {
  readonly spec: GameSpec;
  readonly lesson: Lesson | null;
  readonly phase: LearnPhase;
  readonly drill: Drill | null;
  readonly board: readonly number[];
  readonly legalColumns: readonly number[];
  readonly yourSeat: Seat | null;
  /** Which drill of the set, 1-based, for "2 of 3". */
  readonly drillNumber: number;
  readonly total: number;
  /** Right answers this lesson. */
  readonly correct: number;
  /** Set once the player asks to be shown. */
  readonly shown: number | null;
  /** True when the last wrong answer also lost on the spot. */
  readonly walkedIn: boolean;
  /** Cell of the last move played onto the board, for the marker. */
  readonly lastMoveCell: number | null;
  readonly nextReady: boolean;
  readonly error: string | null;
}

interface LearnState {
  readonly spec: GameSpec;
  readonly lessonId: LessonId | null;
  readonly phase: LearnPhase;
  readonly drill: Drill | null;
  readonly game: State<Rules>;
  readonly drillNumber: number;
  readonly correct: number;
  readonly shown: number | null;
  readonly walkedIn: boolean;
  /** The move being judged, so a right answer can be marked where it was played. */
  readonly attempt: number | null;
  /** Hunted ahead of time, waiting for the next drill. */
  readonly queued: Drill | null;
  /** Rises whenever the question changes, to void in-flight replies. */
  readonly token: number;
  readonly seed: number;
  readonly error: string | null;
}

type Action =
  | { readonly type: 'choose'; readonly lesson: LessonId }
  | { readonly type: 'begin' }
  | { readonly type: 'drilled'; readonly drill: Drill | null }
  | { readonly type: 'answer'; readonly param: number }
  | { readonly type: 'judged'; readonly verdict: DrillVerdict }
  | { readonly type: 'failed'; readonly message: string }
  | { readonly type: 'show' }
  | { readonly type: 'again' }
  | { readonly type: 'next' }
  | { readonly type: 'menu' };

function emptyGame(spec: GameSpec): State<Rules> {
  return createInitial(spec.rules, spec.rules.seats[0] as SeatOf<Rules>);
}

/** Replay a drill's setup, falling back to an empty board on bad input. */
function boardOf(spec: GameSpec, drill: Drill | null): State<Rules> {
  if (drill === null) return emptyGame(spec);
  let state = createInitial(spec.rules, drill.firstSeat as SeatOf<Rules>);
  for (const param of drill.setup) {
    if (state.status.kind !== 'in_play') break;
    const applied = applyMove(spec.rules, state, state.toMove, { param });
    if (!applied.ok) break;
    state = applied.state;
  }
  return state;
}

export function createLearn(spec: GameSpec, seed = 1): LearnState {
  return {
    spec,
    lessonId: null,
    phase: 'choosing',
    drill: null,
    game: emptyGame(spec),
    drillNumber: 0,
    correct: 0,
    shown: null,
    walkedIn: false,
    attempt: null,
    queued: null,
    token: 0,
    seed,
    error: null,
  };
}

/** Put a drill on the board. */
function mount(state: LearnState, drill: Drill): LearnState {
  return {
    ...state,
    drill,
    phase: 'asking',
    game: boardOf(state.spec, drill),
    drillNumber: state.drillNumber + 1,
    shown: null,
    walkedIn: false,
    attempt: null,
    token: state.token + 1,
    error: null,
  };
}

export function learnReducer(state: LearnState, action: Action): LearnState {
  switch (action.type) {
    /*
     * Choosing shows the idea rather than a board. The naming is the lesson:
     * dropping someone straight onto a position would make this another puzzle
     * screen, and they would never learn what the thing they found is called.
     */
    case 'choose':
      return {
        ...createLearn(state.spec, state.seed + 1),
        lessonId: action.lesson,
        phase: 'reading',
        token: state.token + 1,
      };

    case 'begin': {
      if (state.lessonId === null) return state;
      // The head start paid off: straight to the board, with no second hunt.
      const ready = state.queued;
      if (ready !== null) return { ...mount(state, ready), queued: null };
      return { ...state, phase: 'seeking', token: state.token + 1 };
    }

    case 'drilled': {
      if (action.drill === null) {
        // Only fatal with nothing on the board; mid-lesson it just means the
        // prefetch found nothing this time.
        return state.drill === null && state.phase === 'seeking'
          ? { ...state, phase: 'stuck' }
          : state;
      }
      /*
       * A drill arriving while the idea is still on screen is the head start
       * landing. It waits rather than interrupting: the player is reading, and
       * yanking a board in front of them mid-sentence would undo the one pause
       * this screen deliberately has.
       */
      if (state.phase === 'reading') return { ...state, queued: action.drill };
      // A drill arriving while one is being answered is the prefetch landing.
      if (state.drill !== null && state.phase !== 'seeking') {
        return { ...state, queued: action.drill };
      }
      return mount(state, action.drill);
    }

    case 'answer': {
      if (state.phase !== 'asking' || state.drill === null) return state;
      // The board is the authority on legality: a tap on a full column is not
      // an answer and must not be judged as one.
      const legal = legalMoves(state.spec.rules, state.game).some(
        (move) => move.param === action.param,
      );
      if (!legal) return state;
      return { ...state, phase: 'checking', attempt: action.param, token: state.token + 1 };
    }

    case 'judged': {
      if (state.drill === null) return state;
      if (action.verdict.kind === 'right') {
        return {
          ...state,
          phase: 'right',
          // Only unaided answers count, so the tally stays worth reading.
          correct: state.shown === null ? state.correct + 1 : state.correct,
          /*
           * Mark where the answer was, on the square they actually chose rather
           * than on the drill's first stored answer: several moves often
           * qualify, and pointing at a different one would read as a
           * correction to a move that was right.
           */
          shown: state.attempt ?? state.shown,
        };
      }
      if (action.verdict.kind === 'wrong') {
        return { ...state, phase: 'wrong', walkedIn: action.verdict.walkedIn };
      }
      return { ...state, phase: 'asking' };
    }

    case 'show': {
      if (state.drill === null) return state;
      if (state.phase !== 'asking' && state.phase !== 'wrong') return state;
      // The answer is already known: it was computed when the drill was found,
      // and for these lessons it is a property of the position rather than of
      // a line, so it cannot go stale the way a puzzle hint could.
      return { ...state, phase: 'asking', shown: state.drill.answers[0] ?? null };
    }

    case 'again':
      if (state.phase !== 'wrong') return state;
      return { ...state, phase: 'asking', walkedIn: false };

    case 'next': {
      if (state.drillNumber >= DRILLS_PER_LESSON) {
        return { ...state, phase: 'finished' };
      }
      if (state.queued !== null) {
        return mount({ ...state, queued: null }, state.queued);
      }
      return { ...state, phase: 'seeking', drill: null, token: state.token + 1 };
    }

    /*
     * Back to the list.
     *
     * The token must keep climbing across the reset. It is what the effects use
     * to tell "already asked" from "asking again", and `createLearn` starts it
     * at zero - so a plain reset handed out a token the previous lesson had
     * already spent, the hunt effect saw its own past work and never fired, and
     * the second lesson you picked sat on "Finding a position" forever.
     */
    case 'menu':
      return { ...createLearn(state.spec, state.seed + 1), token: state.token + 1 };

    case 'failed':
      return { ...state, phase: state.drill === null ? 'stuck' : 'asking', error: action.message };
  }
}

export interface UseLessonsOptions {
  readonly spec: GameSpec;
  readonly createWorker?: () => Worker;
  /** Overrides the hunt budget. Tests use it to keep the suite quick. */
  readonly budgetMs?: number;
}

export interface LearnControls {
  readonly view: LearnView;
  readonly choose: (lesson: LessonId) => void;
  readonly begin: () => void;
  readonly answer: (param: number) => void;
  readonly show: () => void;
  readonly again: () => void;
  readonly next: () => void;
  readonly menu: () => void;
}

type DrillMessage = Extract<LearnRequest, { kind: 'drill' }>;
type AnswerMessage = Extract<LearnRequest, { kind: 'answer' }>;

export function useLessons(options: UseLessonsOptions): LearnControls {
  const { spec } = options;
  const [state, dispatch] = useReducer(learnReducer, undefined, () => createLearn(spec));

  const workerRef = useRef<Worker | null>(null);
  const create = options.createWorker;

  useEffect(() => {
    const worker = create
      ? create()
      : new Worker(new URL('./learn.worker.ts', import.meta.url), { type: 'module' });
    workerRef.current = worker;

    const onMessage = (event: MessageEvent<LearnResponse>) => {
      const message = event.data;
      if (message.kind === 'drilled') dispatch({ type: 'drilled', drill: message.drill });
      else if (message.kind === 'answered') dispatch({ type: 'judged', verdict: message.verdict });
      else dispatch({ type: 'failed', message: message.message });
    };
    worker.addEventListener('message', onMessage);

    return () => {
      worker.removeEventListener('message', onMessage);
      worker.terminate();
      workerRef.current = null;
    };
  }, [create]);

  /** The hunt already requested, so one need makes one request. */
  const hunted = useRef(-1);

  useEffect(() => {
    const worker = workerRef.current;
    if (!worker) return;
    /*
     * Hunt during the idea card too, not only after "Show me a position".
     *
     * The parity drill needs exact search over rare positions and takes about
     * eleven seconds to find; the reading pause is roughly that long and is
     * otherwise dead time. Starting here spends the wait on something the
     * player was going to do anyway, and the cheap lessons are unaffected
     * because they were already instant.
     */
    if (state.lessonId === null) return;
    if (state.phase !== 'seeking' && state.phase !== 'reading') return;
    if (hunted.current === state.token) return;
    hunted.current = state.token;

    const request: DrillMessage = {
      kind: 'drill',
      rules: spec.rules,
      lesson: state.lessonId,
      seed: state.seed * 1013 + state.drillNumber * 7 + 1,
      ...(options.budgetMs === undefined ? {} : { budgetMs: options.budgetMs }),
      token: state.token,
    };
    worker.postMessage(request);
  }, [spec, state.phase, state.lessonId, state.token, state.seed, state.drillNumber, options.budgetMs]);

  /*
   * Hunt the next drill while this one is being answered.
   *
   * Fired once a drill is up rather than when the player answers, so the whole
   * time they spend thinking is time the engine is already working.
   */
  const prefetched = useRef<string | null>(null);

  useEffect(() => {
    const worker = workerRef.current;
    if (!worker) return;
    if (state.drill === null || state.lessonId === null) return;
    if (state.queued !== null) return;
    if (state.drillNumber >= DRILLS_PER_LESSON) return;
    if (prefetched.current === state.drill.id) return;
    prefetched.current = state.drill.id;

    const request: DrillMessage = {
      kind: 'drill',
      rules: spec.rules,
      lesson: state.lessonId,
      seed: state.seed * 1013 + (state.drillNumber + 1) * 7 + 1,
      ...(options.budgetMs === undefined ? {} : { budgetMs: options.budgetMs }),
      token: state.token,
    };
    worker.postMessage(request);
  }, [
    spec,
    state.drill,
    state.lessonId,
    state.queued,
    state.drillNumber,
    state.seed,
    state.token,
    options.budgetMs,
  ]);

  /** The answer already sent, so one tap makes one request. */
  const judged = useRef(-1);
  const pending = useRef<number | null>(null);

  const answer = useCallback((param: number) => {
    pending.current = param;
    dispatch({ type: 'answer', param });
  }, []);

  useEffect(() => {
    const worker = workerRef.current;
    if (!worker) return;
    if (state.phase !== 'checking' || state.drill === null || state.lessonId === null) return;
    if (judged.current === state.token) return;
    judged.current = state.token;

    const param = pending.current;
    if (param === null) return;

    const request: AnswerMessage = {
      kind: 'answer',
      rules: spec.rules,
      lesson: state.lessonId,
      firstSeat: state.drill.firstSeat,
      setup: state.drill.setup,
      param,
      token: state.token,
    };
    worker.postMessage(request);
  }, [spec, state.phase, state.drill, state.lessonId, state.token]);

  const view = useMemo<LearnView>(() => {
    const rules = spec.rules;
    const live = state.drill !== null && state.game.status.kind === 'in_play';
    const seat = state.drill === null ? null : (state.drill.seat as Seat);

    return {
      spec,
      lesson: state.lessonId === null ? null : LESSONS[state.lessonId],
      phase: state.phase,
      drill: state.drill,
      board: state.game.board as readonly number[],
      // Answering is pointing at a square, so the columns are live only while
      // a question is open.
      legalColumns:
        live && state.phase === 'asking' ? legalMoves(rules, state.game).map((m) => m.param) : [],
      yourSeat: seat,
      drillNumber: state.drillNumber,
      total: DRILLS_PER_LESSON,
      correct: state.correct,
      shown: state.shown,
      walkedIn: state.walkedIn,
      lastMoveCell: null,
      nextReady: state.queued !== null,
      error: state.error,
    };
  }, [spec, state]);

  return {
    view,
    choose: useCallback((lesson: LessonId) => dispatch({ type: 'choose', lesson }), []),
    begin: useCallback(() => dispatch({ type: 'begin' }), []),
    answer,
    show: useCallback(() => dispatch({ type: 'show' }), []),
    again: useCallback(() => dispatch({ type: 'again' }), []),
    next: useCallback(() => dispatch({ type: 'next' }), []),
    menu: useCallback(() => dispatch({ type: 'menu' }), []),
  };
}
